import { loadImageBitmap } from './media-utils';
import { createModelViewer, getModelViewerCanvas, releaseModelViewer } from './three-viewer';
import type { StudioAsset, StudioClip, StudioTrack } from './types';

export interface DirectedShot {
  start: number;
  duration: number;
  title: string;
  subtitle: string;
  color: string;
}

export interface RenderFrame {
  assets: Map<string, StudioAsset>;
  tracks: StudioTrack[];
  time: number;
  width: number;
  height: number;
  backgroundColor?: string;
  directedShots?: DirectedShot[];
}

const VIDEO_CACHE = new Map<string, HTMLVideoElement>();
const IMAGE_CACHE = new Map<string, ImageBitmap | HTMLImageElement>();

const SHOT_COLORS = ['#101c2e', '#2e1418', '#14241a', '#2e2a14', '#1a142e', '#0f2626'];

export function emotionColor(emotion: string | undefined, index: number): string {
  switch (emotion) {
    case 'suspense':
      return '#1c0f16';
    case 'joy':
      return '#1c2410';
    case 'contemplation':
      return '#0f1820';
    case 'wonder':
      return '#0e1a28';
    default:
      return SHOT_COLORS[index % SHOT_COLORS.length];
  }
}

export interface ShotSource {
  start: number;
  duration: number;
  purpose?: string;
  emotion?: string;
  typography?: Array<{ text: string }>;
}

export function buildDirectedShots(
  scenes: Array<{ duration: number; shots: ShotSource[] }> | null
): DirectedShot[] {
  if (!scenes) return [];
  const shots: DirectedShot[] = [];
  let cursor = 0;
  for (const scene of scenes) {
    for (const shot of scene.shots) {
      shots.push({
        start: cursor + shot.start,
        duration: shot.duration,
        title: shot.typography?.[0]?.text ?? `Shot ${shots.length + 1}`,
        subtitle: shot.purpose ?? '',
        color: emotionColor(shot.emotion, shots.length),
      });
    }
    cursor += scene.duration;
  }
  return shots;
}

function containRect(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return {
    x: (targetWidth - width) / 2,
    y: (targetHeight - height) / 2,
    width,
    height,
  };
}

export async function loadMedia(
  asset: StudioAsset
): Promise<ImageBitmap | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | null> {
  if (!asset.url) return null;
  if (asset.kind === 'image') {
    const cached = IMAGE_CACHE.get(asset.id);
    if (cached) return cached;
    try {
      const bitmap = await loadImageBitmap(asset.url);
      IMAGE_CACHE.set(asset.id, bitmap);
      return bitmap;
    } catch {
      const img = new Image();
      img.src = asset.url;
      IMAGE_CACHE.set(asset.id, img);
      return img;
    }
  }
  if (asset.kind === 'video') {
    const cached = VIDEO_CACHE.get(asset.id);
    if (cached) return cached;
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = asset.url;
    VIDEO_CACHE.set(asset.id, video);
    return video;
  }
  if (asset.kind === 'glb' || asset.kind === 'gltf') {
    return createModelViewer(asset.id, asset.url);
  }
  return null;
}

export function releaseMedia(assetId: string): void {
  const image = IMAGE_CACHE.get(assetId);
  if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) image.close();
  IMAGE_CACHE.delete(assetId);
  VIDEO_CACHE.delete(assetId);
  releaseModelViewer(assetId);
}

export function drawFrame(ctx: CanvasRenderingContext2D, frame: RenderFrame): void {
  ctx.save();
  ctx.fillStyle = frame.backgroundColor ?? '#0a0a0f';
  ctx.fillRect(0, 0, frame.width, frame.height);
  ctx.restore();

  let drewMedia = false;
  for (const track of frame.tracks) {
    if (track.kind !== 'video') continue;
    const clip = track.clips.find(
      (c) => frame.time >= c.start && frame.time < c.start + c.duration
    );
    if (!clip) continue;
    drawClip(ctx, clip, frame);
    drewMedia = true;
  }

  if (!drewMedia && frame.directedShots) {
    const shot = frame.directedShots.find(
      (s) => frame.time >= s.start && frame.time < s.start + s.duration
    );
    if (shot) drawDirectedShot(ctx, shot, frame);
  }
}

function drawDirectedShot(
  ctx: CanvasRenderingContext2D,
  shot: DirectedShot,
  frame: RenderFrame
): void {
  ctx.save();
  ctx.fillStyle = shot.color;
  ctx.fillRect(0, 0, frame.width, frame.height);

  const grad = ctx.createRadialGradient(
    frame.width / 2,
    frame.height / 2,
    0,
    frame.width / 2,
    frame.height / 2,
    Math.max(frame.width, frame.height) * 0.7
  );
  grad.addColorStop(0, 'rgba(255,255,255,0.06)');
  grad.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, frame.width, frame.height);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#f4f4f8';
  ctx.font = `600 ${Math.round(frame.width * 0.045)}px Inter, sans-serif`;
  ctx.fillText(shot.title, frame.width / 2, frame.height * 0.44);

  if (shot.subtitle) {
    ctx.fillStyle = 'rgba(244,244,248,0.75)';
    ctx.font = `400 ${Math.round(frame.width * 0.018)}px Inter, sans-serif`;
    ctx.fillText(shot.subtitle, frame.width / 2, frame.height * 0.5);
  }
  ctx.restore();
}

function drawClip(ctx: CanvasRenderingContext2D, clip: StudioClip, frame: RenderFrame): void {
  const asset = frame.assets.get(clip.assetId);
  if (!asset || !asset.url) {
    drawPlaceholder(ctx, clip, frame, '#3a1d2e');
    return;
  }
  const local = (frame.time - clip.start + clip.trimStart) % (asset.duration ?? clip.duration);
  const media = getCachedMedia(asset);
  if (media instanceof HTMLVideoElement) {
    media.currentTime = local;
    drawElement(ctx, media, frame);
  } else if (media) {
    drawElement(ctx, media, frame);
  } else {
    drawPlaceholder(ctx, clip, frame, '#26263a');
  }
}

function getCachedMedia(
  asset: StudioAsset
): ImageBitmap | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | null {
  if (asset.kind === 'video') return VIDEO_CACHE.get(asset.id) ?? null;
  if (asset.kind === 'image') return IMAGE_CACHE.get(asset.id) ?? null;
  if (asset.kind === 'glb' || asset.kind === 'gltf') return getModelViewerCanvas(asset.id);
  return null;
}

function drawElement(
  ctx: CanvasRenderingContext2D,
  media: ImageBitmap | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  frame: RenderFrame
): void {
  if (typeof HTMLVideoElement !== 'undefined' && media instanceof HTMLVideoElement) {
    if (media.videoWidth === 0) return;
    const rect = containRect(media.videoWidth, media.videoHeight, frame.width, frame.height);
    ctx.drawImage(media, rect.x, rect.y, rect.width, rect.height);
  } else if (media instanceof HTMLCanvasElement) {
    const rect = containRect(media.width, media.height, frame.width, frame.height);
    ctx.drawImage(media, rect.x, rect.y, rect.width, rect.height);
  } else {
    const img = media as ImageBitmap | HTMLImageElement;
    let naturalWidth: number;
    let naturalHeight: number;
    if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
      naturalWidth = img.width;
      naturalHeight = img.height;
    } else {
      const htmlImg = img as HTMLImageElement;
      naturalWidth = htmlImg.naturalWidth;
      naturalHeight = htmlImg.naturalHeight;
    }
    if (naturalWidth === 0) return;
    const rect = containRect(naturalWidth, naturalHeight, frame.width, frame.height);
    ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
  }
}

function drawPlaceholder(
  ctx: CanvasRenderingContext2D,
  clip: StudioClip,
  frame: RenderFrame,
  color: string
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, frame.width, frame.height);
  ctx.fillStyle = '#9a9aa8';
  ctx.font = '28px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(clip.assetId, frame.width / 2, frame.height / 2);
  ctx.restore();
}
